import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import moment from 'moment';
import { TranslateService } from '@ngx-translate/core';
import {
  StaffAttendanceApiService,
  AttendanceStudent,
  AttendanceStatus,
  PeriodListItem,
} from '../services/staff-attendance-api.service';
import { StaffSessionService } from '../services/staff-session.service';

declare var customToastr: any;

const DATE_FORMAT = 'YYYY-MM-DD';

// Arrives via router state when landing here from the period-wise-attendance
// picker (StaffPeriodWiseAttendanceComponent) — a specific period already
// chosen, plus the date that was active on that screen. Absent when arriving
// straight from ClassView for a DAY-based class (see attendanceType branch
// in StaffClassOverviewComponent.openTile).
export interface StaffClassAttendanceNavState {
  periodId: string | number;
  selectedDate: string;
}

@Component({
  selector: 'app-staff-class-attendance',
  templateUrl: './staff-class-attendance.component.html',
  styleUrls: ['../shared/staff-common.css', './staff-class-attendance.component.css']
})
export class StaffClassAttendanceComponent implements OnInit {
  classId: string | null;
  staffId: string | number | null = null;

  isPeriodBased = false;
  periods: PeriodListItem[] = [];
  selectedPeriodId: string | null = null;

  // True only when this screen was reached via the period-picker — governs
  // where the back button/link goes (per spec: only the Period path returns
  // to the picker; the direct DAY path returns to ClassView).
  private arrivedViaPeriodPicker = false;

  selectedDateObj: Date = new Date();
  maxDate: Date = new Date();

  students: AttendanceStudent[] = [];
  studentsOnLeaveIds = new Set<number>();
  pendingLeaveIds = new Set<number>();

  allowedToMarkAttendance = true;
  attendanceTaken = false;
  isChanged = false;

  showConfirmModal = false;
  confirmDateLabel = '';
  confirmTimeLabel = '';

  loading = true;
  submitting = false;
  loadError = false;
  readonly skeletonPlaceholders = Array.from({ length: 8 }, () => ({}));

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private staffAttendanceApi: StaffAttendanceApiService,
    private staffSession: StaffSessionService,
    private translate: TranslateService
  ) {}

  get selectedDate(): string {
    return moment(this.selectedDateObj).format(DATE_FORMAT);
  }

  ngOnInit(): void {
    this.classId = this.route.snapshot.paramMap.get('id');
    const staffDetails = this.staffSession.getStaffDetails();
    this.staffId = staffDetails?.staffId ?? null;

    // Today, in the browser's local time — this app has no moment-timezone
    // dependency, so region-aware date math (as the mobile app does via
    // moment().tz(region)) isn't available; local time is the existing
    // convention for every other date in this app. No lower bound is
    // enforced client-side either way — the backend gates past-date
    // marking via allowedToMarkAttendance instead.
    this.maxDate = new Date();

    const cachedClass = this.staffSession.getClassData();
    this.isPeriodBased = cachedClass?.attendanceType === 'PERIOD';

    const state = history.state as StaffClassAttendanceNavState;
    if (state?.periodId) {
      this.arrivedViaPeriodPicker = true;
      this.isPeriodBased = true;
      this.selectedPeriodId = String(state.periodId);
      if (state.selectedDate) {
        this.selectedDateObj = moment(state.selectedDate, DATE_FORMAT).toDate();
      }
      // Still load the period list so the tab strip is available to switch
      // periods after arrival — just don't let it override the period the
      // picker screen already chose.
      this.loadPeriodList(/* preserveSelectedPeriod */ true);
      return;
    }

    this.refresh();
  }

  refresh() {
    if (this.isPeriodBased) {
      this.loadPeriodList();
    } else {
      this.loadStudentList();
    }
  }

  onDateChange() {
    this.selectedPeriodId = null;
    this.refresh();
  }

  // Only screens reached via the period picker return to it — the direct
  // DAY path never passed through a period-selection step, so it returns
  // straight to ClassView (per spec).
  goBack() {
    if (this.arrivedViaPeriodPicker) {
      this.router.navigate(['/staff/class', this.classId, 'period-wise-attendance']);
      return;
    }
    this.router.navigate(['/staff/class', this.classId]);
  }

  async loadPeriodList(preserveSelectedPeriod = false) {
    if (!this.staffId || !this.classId) {
      this.loading = false;
      this.loadError = true;
      return;
    }
    this.loading = true;
    this.loadError = false;
    try {
      const response = await firstValueFrom(
        this.staffAttendanceApi.getPeriodList(String(this.staffId), this.classId, this.selectedDate)
      );
      const responseDetails = response as any;
      if (responseDetails.status && responseDetails.body) {
        this.periods = responseDetails.body.periodListData || [];
        if (preserveSelectedPeriod && this.selectedPeriodId) {
          this.loadStudentList();
        } else if (this.periods.length) {
          this.selectPeriod(this.periods[0]);
        } else {
          this.loading = false;
        }
      } else {
        this.loadError = true;
        this.loading = false;
      }
    } catch (error) {
      this.loadError = true;
      this.loading = false;
    }
  }

  selectPeriod(period: PeriodListItem) {
    this.selectedPeriodId = period.periodId ?? period.id ?? null;
    this.loadStudentList();
  }

  async loadStudentList() {
    if (!this.staffId || !this.classId) {
      this.loading = false;
      this.loadError = true;
      return;
    }
    this.loading = true;
    this.loadError = false;
    try {
      const response = await firstValueFrom(
        this.staffAttendanceApi.getStudentList(
          String(this.staffId),
          this.classId,
          this.selectedDate,
          this.isPeriodBased ? this.selectedPeriodId : null
        )
      );
      const responseDetails = response as any;
      if (responseDetails.status && responseDetails.body) {
        const { studentList, studentsOnLeaveIds, pendingLeaveIds } = this.staffAttendanceApi.mergeStudentGroups(responseDetails.body);
        this.students = studentList;
        this.studentsOnLeaveIds = studentsOnLeaveIds;
        this.pendingLeaveIds = pendingLeaveIds;
        this.allowedToMarkAttendance = responseDetails.body.allowedToMarkAttendance;
        this.attendanceTaken = responseDetails.body.attendanceTaken;
        this.isChanged = false;
      } else {
        this.loadError = true;
      }
    } catch (error) {
      this.loadError = true;
    } finally {
      this.loading = false;
    }
  }

  markStatus(student: AttendanceStudent, status: AttendanceStatus) {
    student.Attendance = status;
    this.isChanged = true;
  }

  markAllAs(status: AttendanceStatus) {
    this.students.forEach(s => {
      if (!this.studentsOnLeaveIds.has(s.studentId)) {
        s.Attendance = status;
      }
    });
    this.isChanged = true;
  }

  // Opens the confirmation popup rather than submitting immediately — the
  // actual submit only happens once the staff member taps "Yes" there.
  async requestSubmitAttendance() {
    if (!this.staffId || !this.classId) { return; }
    const unmarked = this.students.some(s => s.Attendance === null);
    if (unmarked) {
      const message = await firstValueFrom(this.translate.get('pleaseMarkAttendanceForAllStudents'));
      customToastr(message, 'error');
      return;
    }

    const now = new Date();
    this.confirmDateLabel = moment(now).format('DD MMM,YY');
    this.confirmTimeLabel = moment(now).format('hh:mm a');
    this.showConfirmModal = true;
  }

  cancelSubmitAttendance() {
    this.showConfirmModal = false;
  }

  async confirmSubmitAttendance() {
    this.showConfirmModal = false;
    await this.submitAttendance();
  }

  private async submitAttendance() {
    if (!this.staffId || !this.classId) { return; }
    this.submitting = true;
    try {
      const body = {
        classId: this.classId,
        staffId: String(this.staffId),
        attendanceDate: this.selectedDate,
        periodId: this.isPeriodBased ? this.selectedPeriodId : null,
        lang: 'en',
        attendance: this.students.map(s => ({
          fkStudentId: s.studentId,
          attendance: s.Attendance as 'PRESENT' | 'ABSENT' | 'LATE' | 'LEAVE',
          pkAttendanceId: s.pkAttendanceId,
        })),
      };
      const response = await firstValueFrom(this.staffAttendanceApi.markAttendance(body));
      const responseDetails = response as any;
      if (responseDetails.status && responseDetails.body) {
        customToastr(responseDetails.body.message, 'success');
        this.attendanceTaken = true;
        this.isChanged = false;
      } else {
        customToastr(responseDetails.message, 'error');
      }
    } catch (error) {
      const message = await firstValueFrom(this.translate.get('staffSomethingWentWrong'));
      customToastr(message, 'error');
    } finally {
      this.submitting = false;
    }
  }

  isOnApprovedLeave(student: AttendanceStudent): boolean {
    return this.studentsOnLeaveIds.has(student.studentId);
  }

  isOnPendingLeave(student: AttendanceStudent): boolean {
    return this.pendingLeaveIds.has(student.studentId);
  }

  private studentGroup(student: AttendanceStudent): 'approved' | 'pending' | 'regular' {
    if (this.isOnApprovedLeave(student)) { return 'approved'; }
    if (this.isOnPendingLeave(student)) { return 'pending'; }
    return 'regular';
  }

  // students[] is already ordered Approved Leave -> Pending Leave -> Regular
  // (see StaffAttendanceApiService.mergeStudentGroups), so a section boundary
  // is just wherever the group changes from the previous row — no separate
  // grouped data structure needed for the divider itself.
  sectionLabelBefore(index: number): string | null {
    const group = this.studentGroup(this.students[index]);
    const previousGroup = index > 0 ? this.studentGroup(this.students[index - 1]) : null;
    if (group === previousGroup) { return null; }
    if (group === 'approved') { return 'approvedLeave'; }
    if (group === 'pending') { return 'pendingLeaveRequest'; }
    return this.studentsOnLeaveIds.size > 0 || this.pendingLeaveIds.size > 0 ? 'otherStudents' : null;
  }
}
